import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { toast } from "react-toastify";

import { selectCurrentLanguage } from "features/language/languageSlice";

import {
  PortalUnreachableError,
  refreshToken,
  getAccessToken,
} from "components/utilities/sedra3/auth";

const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_BASE_URL,
  // The session's cookies come in the answers of the sign-in, the password
  // change and the logout (#112). In development the API is another origin than
  // the page, and the browser keeps or sends a cookie of a request to it only
  // when asked to include its credentials; on the servers both are one origin.
  credentials: "include",

  prepareHeaders: (headers, { getState }) => {
    const token = getAccessToken();
    const state = getState();
    const currentLanguage = selectCurrentLanguage(state);

    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    if (currentLanguage) {
      headers.set("X-Accept-Language", currentLanguage);
    }
    return headers;
  },
});

// The API answers 401 when the token cannot be used (expired, revoked after a
// logout or a renewal, broken): renewing may help. A 403 is a signed-in user
// without the permission; renewing would not change it, so it goes to the
// calling component, which shows it, and the user stays signed in.

export const baseQueryWithReauth = async (args, api, extraOptions) => {
  // The token this request leaves with (prepareHeaders reads the same one).
  const sentWith = getAccessToken();
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    try {
      // Refused with a token another query has already replaced: retry with
      // the current one. Renewing again would revoke the pair just issued and
      // fail the queries retrying with it.
      const current = getAccessToken();
      const newToken =
        current && current !== sentWith ? current : await refreshToken();
      // Retry the original query with the new token
      if (newToken) {
        const retryArgs = {
          ...args,
          headers: {
            ...(args.headers || {}),
            Authorization: `Bearer ${newToken}`,
          },
        };

        result = await baseQuery(retryArgs, api, extraOptions);
      }
    } catch (error) {
      console.error("Token refresh failed", error);
      // The renewal judges its own failure (auth.js, #121). Refused by the
      // server (logout, password reset, expiry), it has signed the user out —
      // the access token in memory, the session's records, the API caches —
      // and the signed-in pages send the browser to the sign-in page;
      // the 401 stands. Not answered (no answer, a 5xx), the session is still
      // alive: this request fails with the plain message and may be tried again.
      if (error instanceof PortalUnreachableError) {
        result = {
          error: {
            status: "CUSTOM_ERROR",
            error: error.message,
            data: { detail: error.message },
          },
        };
      }
    }
  }

  // A read answered 409 asks for a stored record the portal cannot show (a value its schema
  // refuses): the server says what to do about it, and every page shows that, once (#136).
  // A write's 409 (a change already made) is the calling page's to show.
  const method = (
    typeof args === "string" ? "GET" : args?.method || "GET"
  ).toUpperCase();
  if (result.error?.status === 409 && method === "GET") {
    const detail = result.error.data?.detail;
    if (typeof detail === "string" && detail) {
      toast.error(detail, {
        toastId: detail,
        theme: "colored",
        autoClose: 6000,
      });
    }
  }

  return result;
};

export const authApi = createApi({
  reducerPath: "authApi",
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    loginUser: builder.mutation({
      query: (credentials) => ({
        url: "/login",
        method: "POST",
        body: credentials,
      }),
    }),
    getBanks: builder.query({
      query: () => "/bank/list",
    }),
    getRolesByBank: builder.query({
      query: (bankId) => ({
        url: `/role/${bankId}/roles`,
        method: "GET",
        headers: {
          // Add any custom headers here if needed
        },
      }),
    }),
    registerUser: builder.mutation({
      query: (userDetails) => ({
        url: "/user",
        method: "POST",
        body: userDetails,
      }),
    }),
    openPasswordResetLink: builder.mutation({
      query: (linkToken) => ({
        url: "/login/reset-password/open",
        method: "POST",
        body: linkToken,
      }),
    }),
    resetPassword: builder.mutation({
      query: (passwordResetData) => ({
        url: "/login/reset-password",
        method: "POST",
        body: passwordResetData,
      }),
    }),
    passwordResetRequest: builder.mutation({
      query: (email) => ({
        url: "/login/password-reset-request",
        method: "POST",
        body: { email },
      }),
    }),
    changePassword: builder.mutation({
      query: (passwordChangeData) => ({
        url: "/login/change_password",
        method: "POST",
        body: passwordChangeData,
      }),
    }),
    logoutUser: builder.mutation({
      query: () => ({
        url: "/login/logout",
        method: "POST",
      }),
    }),
  }),
});

export const {
  useLoginUserMutation,
  useRegisterUserMutation,
  useGetBanksQuery,
  useGetRolesByBankQuery,
  useOpenPasswordResetLinkMutation,
  useResetPasswordMutation,
  usePasswordResetRequestMutation,
  useChangePasswordMutation,
  useLogoutUserMutation,
} = authApi;
